// Create the provider component
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { FuseFlatNavItemType, FuseNavItemType } from '@fuse/core/FuseNavigation/types/FuseNavItemType';
import FuseNavigationHelper from '@fuse/utils/FuseNavigationHelper';
import navigationConfig from '@/configs/navigationConfig';
import FuseNavItemModel from '@fuse/core/FuseNavigation/models/FuseNavItemModel';
import { PartialDeep } from 'type-fest';
import { NavigationContext } from '@/components/theme-layouts/components/navigation/contexts/NavigationContext';
import { orchestratorApi } from '@/app/services/orchestrator-api.service';

// Runtime nav filtering by user:
//  - the 'admin' group is admin-only — superadmin gets all of it, a company admin
//    (isCompAdmin, not superadmin) gets only the Users item, matching what AdminLayout actually
//    lets them reach. Everyone else doesn't see the group at all.
//  - Phase 2 items (those carrying `requiresFeature`) are internal Kairos staff only
//    (the env flag itself is applied at build time in navigationConfig.ts)
function filterNavForUser(isAdmin: boolean, isCompAdmin: boolean, isInternal: boolean): FuseNavItemType[] {
	return navigationConfig
		.filter((item) => {
			if (item.id === 'admin' && !isAdmin && !isCompAdmin) return false;
			if ((item as { requiresFeature?: string }).requiresFeature && !isInternal) return false;
			return true;
		})
		.map((item) => {
			if (item.id === 'admin' && !isAdmin && isCompAdmin) {
				return { ...item, children: item.children?.filter((child) => child.id === 'admin-users') };
			}
			return item;
		});
}

function getNavItems(): FuseFlatNavItemType[] {
	const isAdmin = typeof window !== 'undefined' && orchestratorApi.getIsAdmin();
	const isCompAdmin = typeof window !== 'undefined' && orchestratorApi.getIsCompAdmin();
	const isInternal = typeof window !== 'undefined' && orchestratorApi.getIsInternalUser();
	return FuseNavigationHelper.flattenNavigation(filterNavForUser(isAdmin, isCompAdmin, isInternal));
}

export function NavigationContextProvider({ children }: { children: ReactNode }) {
	// Safe default before the client-side checks run: no admin, not internal
	// (hides the admin group and any Phase 2 items until resolved on mount).
	const [navigationItems, setNavigationItems] = useState<FuseFlatNavItemType[]>(
		FuseNavigationHelper.flattenNavigation(filterNavForUser(false, false, false))
	);

	useEffect(() => {
		setNavigationItems(getNavItems());
	}, []);

	const setNavigation = useCallback((items: FuseNavItemType[]) => {
		setNavigationItems(FuseNavigationHelper.flattenNavigation(items));
	}, []);

	const appendNavigationItem = useCallback(
		(item: FuseNavItemType, parentId?: string | null) => {
			const navigation = FuseNavigationHelper.unflattenNavigation(navigationItems);
			setNavigation(FuseNavigationHelper.appendNavItem(navigation, FuseNavItemModel(item), parentId));
		},
		[navigationItems, setNavigation]
	);

	const prependNavigationItem = useCallback(
		(item: FuseNavItemType, parentId?: string | null) => {
			const navigation = FuseNavigationHelper.unflattenNavigation(navigationItems);
			setNavigation(FuseNavigationHelper.prependNavItem(navigation, FuseNavItemModel(item), parentId));
		},
		[navigationItems, setNavigation]
	);

	const updateNavigationItem = useCallback(
		(id: string, item: PartialDeep<FuseNavItemType>) => {
			const navigation = FuseNavigationHelper.unflattenNavigation(navigationItems);
			setNavigation(FuseNavigationHelper.updateNavItem(navigation, id, item));
		},
		[navigationItems, setNavigation]
	);

	const removeNavigationItem = useCallback(
		(id: string) => {
			const navigation = FuseNavigationHelper.unflattenNavigation(navigationItems);
			setNavigation(FuseNavigationHelper.removeNavItem(navigation, id));
		},
		[navigationItems, setNavigation]
	);

	const resetNavigation = useCallback(() => {
		setNavigationItems(getNavItems());
	}, []);

	const getNavigationItemById = useCallback(
		(id: string) => navigationItems.find((item) => item.id === id),
		[navigationItems]
	);

	const value = {
		setNavigation,
		navigationItems,
		appendNavigationItem,
		prependNavigationItem,
		updateNavigationItem,
		removeNavigationItem,
		resetNavigation,
		getNavigationItemById
	};

	return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}
